import React, { useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  greenEmerald: "#44aa5a",
  textPrimary: "#f9f9cf",
  textSecondary: "#99a1af",
  textMuted: "#6a7282",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  borderGreen: "rgba(98,244,129,0.3)",
  divider: "rgba(98,244,129,0.2)",
  red: "#ff6467",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: DL.bgLight,
    borderRadius: "8px",
    "& fieldset": { borderColor: DL.border },
    "&:hover fieldset": { borderColor: DL.borderSubtle },
    "&.Mui-focused fieldset": { borderColor: DL.green },
  },
  "& .MuiInputBase-input": { color: DL.textPrimary, fontSize: "0.9rem" },
  "& .MuiInputLabel-root": { color: DL.textMuted, fontSize: "0.9rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: DL.green },
};

function ForgotUsernamePage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/auth/forgot-username`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(data.message);
      } else {
        setErrorMessage(data.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setErrorMessage("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: DL.bgDark,
        background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.06) 0%, ${DL.bgDark} 60%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      {/* Back link */}
      <Box sx={{ width: "100%", maxWidth: 480, mb: 2 }}>
        <Button
          onClick={() => navigate("/signin")}
          sx={{
            color: DL.textSecondary,
            textTransform: "none",
            fontSize: "0.875rem",
            p: 0,
            "&:hover": { color: DL.textPrimary, backgroundColor: "transparent" },
          }}
          disableRipple
        >
          ← Back to sign in
        </Button>
      </Box>

      {/* Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 480,
          backgroundColor: DL.bgMedium,
          border: `1px solid ${DL.border}`,
          borderRadius: "12px",
          p: 4,
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: "1.5rem",
            color: DL.textPrimary,
            textAlign: "center",
            mb: 1,
          }}
        >
          Forgot Username
        </Typography>
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: DL.textSecondary,
            textAlign: "center",
            mb: 3,
          }}
        >
          Enter your email address and we'll send you your username.
        </Typography>

        {successMessage && (
          <Box
            sx={{
              mb: 2,
              p: "10px 14px",
              borderRadius: "8px",
              border: `1px solid ${DL.borderGreen}`,
              backgroundColor: "rgba(98,244,129,0.06)",
            }}
          >
            <Typography sx={{ fontSize: "0.875rem", color: DL.green }}>
              {successMessage}
            </Typography>
          </Box>
        )}

        {errorMessage && (
          <Box
            sx={{
              mb: 2,
              p: "10px 14px",
              borderRadius: "8px",
              border: `1px solid rgba(255,100,103,0.35)`,
              backgroundColor: "rgba(255,100,103,0.06)",
            }}
          >
            <Typography sx={{ fontSize: "0.875rem", color: DL.red }}>
              {errorMessage}
            </Typography>
          </Box>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            sx={{ ...inputSx, mb: 1 }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 2,
              mb: 1,
              background: "linear-gradient(to right, #10b981, #059669)",
              color: "#fff",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              width: "100%",
              py: 1.25,
              fontSize: "0.95rem",
              boxShadow: "none",
              "&:hover": {
                background: "linear-gradient(to right, #0ea472, #048556)",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                background: "linear-gradient(to right, #10b981, #059669)",
                opacity: 0.5,
                color: "#fff",
              },
            }}
          >
            {loading ? "Sending..." : "Send Username"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default ForgotUsernamePage;
